import { useCallback, useState } from 'react';

// ---------------------------------------------------------------------------
// useDeviceBinding -- client-side state for a bound device-vehicle token.
//
// plan-responsekit-2026-08-02-member-location-tracking.md, Approach §5
// (IAM-03(b)), Execution step 10.
//
// A device binding token, once entered on this browser (e.g. an
// ambulance-mounted tablet's kiosk browser), is persisted in localStorage
// -- mirroring useStationMode.ts's "station_mode_lock" sticky-device-mode
// precedent (the plan's own cited UX precedent; that hook provides no
// server-side enforcement, and neither does this client-side flag by
// itself -- the actual override happens server-side when the token header
// is presented on a position report, see asset_tracking.py). This hook
// only drives the PERSISTENT, NON-DISMISSABLE client indicator so a crew
// member using a bound browser cannot be unaware of it (IAM-03(b)).
// ---------------------------------------------------------------------------

const TOKEN_KEY = 'rk_device_binding_token';
const VEHICLE_KEY = 'rk_device_binding_vehicle_id';

export interface UseDeviceBindingReturn {
  token: string | null;
  vehicleId: string | null;
  isBound: boolean;
  setBinding: (token: string, vehicleId: string) => void;
  clearBinding: () => void;
}

export function useDeviceBinding(): UseDeviceBindingReturn {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [vehicleId, setVehicleId] = useState<string | null>(() => localStorage.getItem(VEHICLE_KEY));

  const setBinding = useCallback((newToken: string, newVehicleId: string) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(VEHICLE_KEY, newVehicleId);
    setToken(newToken);
    setVehicleId(newVehicleId);
  }, []);

  const clearBinding = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(VEHICLE_KEY);
    setToken(null);
    setVehicleId(null);
  }, []);

  return { token, vehicleId, isBound: !!token, setBinding, clearBinding };
}

/** Header name a bound-device request must present -- kept alongside the
 * hook so ingest call sites use the same literal string. */
export const DEVICE_BINDING_TOKEN_HEADER = 'X-RK-Device-Binding-Token';
